import { createCollapsibleHistoryStack } from "@captions-web/utils/history-stack";
import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

import { createStoreGetters } from "~/stores/root";

import {
  getRandomAppearanceByGender,
  getRandomBackground,
  GREEN_SCREEN_BACKGROUND_APPEARANCE,
} from "../constants/appearances";

export const CHARACTER_LIMIT = 500;
export const MIN_APPEARANCE_PROMPT_LENGTH = 0;
export const MAX_APPEARANCE_PROMPT_LENGTH = 500;

export type AppearanceField = "character" | "background";
export type GenderType = "male" | "female";
export type TabType = "appearance" | "background";

interface BackgroundAppearanceData {
  prompt?: string;
  generatedId?: string;
  isGreenScreen?: boolean;
}

interface CharacterAppearanceData {
  prompt?: string;
  gender?: GenderType;
  generatedId?: string;
}

interface GenerateActorStoreState {
  selectedTab: TabType;
  gender: GenderType;

  backgroundAppearanceStack: BackgroundAppearanceData[];
  characterAppearanceStack: CharacterAppearanceData[];
}

export function createGenerateActorStore() {
  return createStore(
    combine(
      {
        selectedTab: "appearance",
        gender: "male",
        backgroundAppearanceStack: [],
        characterAppearanceStack: [],
      } as GenerateActorStoreState,
      (set, get) => {
        const backgroundStack = createCollapsibleHistoryStack({
          get: () => get().backgroundAppearanceStack,
          set: (value) => set({ backgroundAppearanceStack: value }),
          shouldCollapse: (a, b) => {
            return a.isGreenScreen === b.isGreenScreen && a.generatedId === b.generatedId;
          },
        });

        const characterStack = createCollapsibleHistoryStack({
          get: () => get().characterAppearanceStack,
          set: (value) => set({ characterAppearanceStack: value }),
          shouldCollapse: (a, b) => a.gender === b.gender && a.generatedId === b.generatedId,
        });

        return {
          async init() {
            const characterAppearance = getRandomAppearanceByGender("male");
            const backgroundAppearance = getRandomBackground();

            characterStack.reset({
              prompt: characterAppearance.prompt,
              generatedId: characterAppearance.id,
              gender: "male",
            });

            backgroundStack.reset({
              prompt: backgroundAppearance.prompt,
              generatedId: backgroundAppearance.id,
              isGreenScreen: false,
            });

            set({
              selectedTab: "appearance",
              gender: "male",
            });
          },

          setSelectedTab(tab: TabType) {
            set({ selectedTab: tab });
          },

          setGender(gender: GenderType) {
            const { gender: previousGender } = get();

            if (previousGender === gender && !!characterStack.current()) {
              return;
            }

            set({ gender });

            const previousCharacterForGender = characterStack.findMostRecent(
              (c) => c.gender === gender
            );

            if (previousCharacterForGender) {
              characterStack.update(previousCharacterForGender);
            } else {
              const newAppearance = getRandomAppearanceByGender(gender);
              characterStack.update({
                prompt: newAppearance.prompt,
                generatedId: newAppearance.id,
                gender,
              });
            }
          },

          setCharacterPrompt(prompt: string) {
            const { gender } = get();

            characterStack.update({ prompt, gender });
          },

          setBackgroundPrompt(prompt: string) {
            backgroundStack.update({ prompt });
          },

          toggleGreenScreen(enabled: boolean) {
            if (enabled) {
              backgroundStack.update({
                prompt: GREEN_SCREEN_BACKGROUND_APPEARANCE,
                isGreenScreen: true,
              });
            } else {
              backgroundStack.popUntil((b) => !b.isGreenScreen);
            }
          },

          shuffleAppearance() {
            const { gender, selectedTab } = get();
            const currentBackground = backgroundStack.current();

            if (selectedTab === "appearance") {
              const newAppearance = getRandomAppearanceByGender(gender);
              characterStack.update({
                prompt: newAppearance.prompt,
                generatedId: newAppearance.id,
                gender,
              });
            } else if (!currentBackground?.isGreenScreen) {
              const newBackground = getRandomBackground();
              backgroundStack.update({
                prompt: newBackground.prompt,
                generatedId: newBackground.id,
              });
            }
          },
        };
      }
    )
  );
}

type GenerateActorStore = ReturnType<ReturnType<typeof createGenerateActorStore>["getState"]>;

export const {
  getStore: getGenerateActorStore,
  getStoreAsync: getGenerateActorStoreAsync,
  getStoreApi: getGenerateActorStoreApi,
} = createStoreGetters<GenerateActorStore>("generate-actor");

export const useGenerateActorStore = createUseStoreWithSelectorDeferred(() =>
  getGenerateActorStoreApi()
);
